import { createSlice } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {
  fetchZones,
  createZone,
  updateZone,
  deleteZone,
  fetchZoneDetail,
  fetchZoneCommunes,
  fetchZoneAssignments,
  createAssignment,
  updateAssignment,
  deleteAssignment,
  fetchZoneFormOptions,
  fetchWarehouseOptions,
  fetchAdminZoneOptions,
} from './zonesThunks';

/**
 * Single slice for the whole shipping-zones domain: list, detail and the
 * catalogs backing its forms.
 *
 * Loading flags are deliberately granular. A zone can cover hundreds of
 * thousands of communes, so its coverage is paginated server-side and must
 * reload on its own without blanking the header or the assignments table.
 */
const initialState = {
  // --- List ---
  zones: [],
  totalCount: 0,
  loading: false,
  /** Kept apart from `loading` so the table is not blanked while a modal saves. */
  saving: false,
  filters: {
    search: '',
    page: 1,
    rowsPerPage: 20,
  },

  // --- Detail ---
  zone: null,
  loadingZone: false,

  assignments: [],
  loadingAssignments: false,
  savingAssignment: false,

  communes: [],
  communesCount: 0,
  loadingCommunes: false,
  communesFilters: {
    search: '',
    page: 1,
    rowsPerPage: 50,
  },

  // --- Form options ---
  courierOptions: [],
  adminOptions: [],
  optionsLoaded: false,
  loadingOptions: false,

  warehouseOptions: [],
  warehousesLoaded: false,
  loadingWarehouses: false,

  /** Zones of one admin, for the forms that attach a warehouse to a zone. */
  adminZoneOptions: [],
  adminZoneOptionsLoaded: false,
  loadingAdminZoneOptions: false,

  error: null,
};

const zonesSlice = createSlice({
  name: 'zones',
  initialState,
  reducers: {
    setSearchFilter: (state, action) => {
      state.filters.search = action.payload;
      state.filters.page = 1;
    },
    setPage: (state, action) => {
      state.filters.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.filters.rowsPerPage = action.payload;
      state.filters.page = 1;
    },
    setCommunesSearch: (state, action) => {
      state.communesFilters.search = action.payload;
      state.communesFilters.page = 1;
    },
    setCommunesPage: (state, action) => {
      state.communesFilters.page = action.payload;
    },
    setCommunesRowsPerPage: (state, action) => {
      state.communesFilters.rowsPerPage = action.payload;
      state.communesFilters.page = 1;
    },
    /** Resets the detail view without dropping the cached catalogs. */
    clearZoneDetails: (state) => {
      state.zone = null;
      state.assignments = [];
      state.communes = [];
      state.communesCount = 0;
      state.communesFilters = initialState.communesFilters;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // --- List ---
      .addCase(fetchZones.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchZones.fulfilled, (state, action) => {
        state.loading = false;
        state.zones = action.payload.results || [];
        state.totalCount = action.payload.count || 0;
      })
      .addCase(fetchZones.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // --- Zone CRUD ---
      .addCase(createZone.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createZone.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(createZone.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      .addCase(updateZone.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateZone.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(updateZone.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      .addCase(deleteZone.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(deleteZone.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(deleteZone.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      // --- Detail: header ---
      .addCase(fetchZoneDetail.pending, (state) => {
        state.loadingZone = true;
        state.error = null;
      })
      .addCase(fetchZoneDetail.fulfilled, (state, action) => {
        state.loadingZone = false;
        state.zone = action.payload;
      })
      .addCase(fetchZoneDetail.rejected, (state, action) => {
        state.loadingZone = false;
        state.error = action.payload;
      })

      // --- Detail: coverage ---
      .addCase(fetchZoneCommunes.pending, (state) => {
        state.loadingCommunes = true;
      })
      .addCase(fetchZoneCommunes.fulfilled, (state, action) => {
        state.loadingCommunes = false;
        state.communes = action.payload.results || [];
        state.communesCount = action.payload.count || 0;
      })
      .addCase(fetchZoneCommunes.rejected, (state, action) => {
        state.loadingCommunes = false;
        state.error = action.payload;
      })

      // --- Detail: assignments ---
      .addCase(fetchZoneAssignments.pending, (state) => {
        state.loadingAssignments = true;
      })
      .addCase(fetchZoneAssignments.fulfilled, (state, action) => {
        state.loadingAssignments = false;
        state.assignments = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchZoneAssignments.rejected, (state, action) => {
        state.loadingAssignments = false;
        state.error = action.payload;
      })

      .addCase(createAssignment.pending, (state) => {
        state.savingAssignment = true;
        state.error = null;
      })
      .addCase(createAssignment.fulfilled, (state) => {
        state.savingAssignment = false;
      })
      .addCase(createAssignment.rejected, (state, action) => {
        state.savingAssignment = false;
        state.error = action.payload;
      })

      .addCase(updateAssignment.pending, (state) => {
        state.savingAssignment = true;
        state.error = null;
      })
      .addCase(updateAssignment.fulfilled, (state) => {
        state.savingAssignment = false;
      })
      .addCase(updateAssignment.rejected, (state, action) => {
        state.savingAssignment = false;
        state.error = action.payload;
      })

      .addCase(deleteAssignment.pending, (state) => {
        state.savingAssignment = true;
        state.error = null;
      })
      .addCase(deleteAssignment.fulfilled, (state) => {
        state.savingAssignment = false;
      })
      .addCase(deleteAssignment.rejected, (state, action) => {
        state.savingAssignment = false;
        state.error = action.payload;
      })

      // --- Form options ---
      .addCase(fetchZoneFormOptions.pending, (state) => {
        state.loadingOptions = true;
        state.error = null;
      })
      .addCase(fetchZoneFormOptions.fulfilled, (state, action) => {
        state.loadingOptions = false;
        state.optionsLoaded = true;
        state.courierOptions = action.payload.courierOptions;
        state.adminOptions = action.payload.adminOptions;
      })
      .addCase(fetchZoneFormOptions.rejected, (state, action) => {
        state.loadingOptions = false;
        if (action.payload) state.error = action.payload;
      })

      .addCase(fetchWarehouseOptions.pending, (state) => {
        state.loadingWarehouses = true;
        state.error = null;
      })
      .addCase(fetchWarehouseOptions.fulfilled, (state, action) => {
        state.loadingWarehouses = false;
        state.warehousesLoaded = true;
        state.warehouseOptions = action.payload;
      })
      .addCase(fetchWarehouseOptions.rejected, (state, action) => {
        state.loadingWarehouses = false;
        if (action.payload) state.error = action.payload;
      })

      .addCase(fetchAdminZoneOptions.pending, (state) => {
        state.loadingAdminZoneOptions = true;
        state.error = null;
      })
      .addCase(fetchAdminZoneOptions.fulfilled, (state, action) => {
        state.loadingAdminZoneOptions = false;
        state.adminZoneOptionsLoaded = true;
        state.adminZoneOptions = action.payload;
      })
      .addCase(fetchAdminZoneOptions.rejected, (state, action) => {
        state.loadingAdminZoneOptions = false;
        // Marked as loaded even on failure, so the form can tell "this admin
        // has no zones" apart from "the zones have not arrived yet".
        state.adminZoneOptionsLoaded = true;
        state.adminZoneOptions = [];
        if (action.payload) state.error = action.payload;
      });
  },
});

export const {
  setSearchFilter,
  setPage,
  setRowsPerPage,
  setCommunesSearch,
  setCommunesPage,
  setCommunesRowsPerPage,
  clearZoneDetails,
  clearError,
} = zonesSlice.actions;

const persistConfig = {
  key: 'zones',
  storage,
  whitelist: ['filters'],
};

export default persistReducer(persistConfig, zonesSlice.reducer);
